import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { EditButton } from './EditButton';
import { SettingsRow, SettingsSection } from './SettingsGroup';

// Expected Sales Costs (FIG-322 / REQ-040): the Expected Disposition Cost
// Default, formerly "Configure Global Settings" on the ∑: Variants report.
// Same column (organisations.dr_sku_percentage) and rules (0–100). New
// merchants start at 20% through the column default (FIG-432 / UC-250).

interface ExpectedSalesCostsSectionProps {
  organisationId: string;
  value: number | string | null | undefined;
  canEdit: boolean;
  onUpdated: () => void;
}

const toNumber = (value: ExpectedSalesCostsSectionProps['value']) => {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPercent = (value: number) => `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;

function parsePercent(text: string): number | null {
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text.trim())) return null;
  const value = Number(text);
  return value >= 0 && value <= 100 ? value : null;
}

export const ExpectedSalesCostsSection: React.FC<ExpectedSalesCostsSectionProps> = ({ organisationId, value, canEdit, onUpdated }) => {
  const current = toNumber(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(timer);
  }, [saved]);

  const parsed = parsePercent(draft);
  const invalid = draft.trim() !== '' && parsed === null;

  const save = async () => {
    if (parsed === null) return;
    setSaving(true);
    setSaveError(false);
    try {
      const { error } = await supabase.from('organisations').update({ dr_sku_percentage: parsed }).eq('id', organisationId);
      if (error) throw error;
      setEditing(false);
      setSaved(true);
      onUpdated();
    } catch (error) {
      console.error('Failed to save the Expected Disposition Cost Default', error);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Expected Sales Costs"
      description={'Used by the ∑: Variants report on this merchant\'s Reports tab. Previously "Configure Global Settings" on that report.'}
    >
      <SettingsRow
        label="Expected Disposition Cost Default"
        editing={editing}
        action={canEdit && !editing ? (
          <EditButton
            label="Edit Expected Disposition Cost Default"
            onClick={() => {
              setDraft(String(current));
              setSaveError(false);
              setEditing(true);
            }}
          />
        ) : null}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{formatPercent(current)}</span>
          {saved && <span role="status" className="text-sm text-emerald-700">Saved</span>}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          The default for all of this merchant's variants. Applies to this merchant only (DR: SKU % column, used in ∑: NERV $).
        </div>
        {editing && (
          <div className="mt-3 space-y-3">
            <div className="max-w-[220px] space-y-1.5">
              <Label htmlFor={`esc-${organisationId}`}>Expected Disposition Cost Default (%)</Label>
              <Input
                id={`esc-${organisationId}`}
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-invalid={invalid}
                aria-describedby={invalid ? `esc-${organisationId}-error` : undefined}
              />
              {invalid && <p id={`esc-${organisationId}-error`} role="alert" className="text-xs text-destructive">Enter a number from 0 to 100, with up to two decimals</p>}
            </div>
            {saveError && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <strong>Percentage not saved.</strong> Please try again. The previous value was kept.
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={save} disabled={saving || parsed === null || parsed === current}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save percentage
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}
      </SettingsRow>
    </SettingsSection>
  );
};

export default ExpectedSalesCostsSection;
